const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const EventNames = require('@/lib/Events/EventNames');
const OnlineTask = require('./OnlineTask');
const OnlineTaskStatus = require('@/lib/OnlineQueue/OnlineTaskStatus');
const { basicHydrate, createModelBasicHydrator } = require('@/lib/Hydration/basicHydrator');

/**
 * This is not fully compatible with AbstractRepository (which is really an AbstractEntityRepository)
 * but it has the same methods where applicable.
 */
class OnlineTaskRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {EventBus} eventBus
     */
    constructor(
        dataStore,
        hydrator,
        eventBus
    ) {
        /**
         * @protected
         * @type {DataStore}
         */
        this.dataStore = dataStore;

        /**
         * @protected
         * @type {Hydrator}
         */
        this.hydrator = hydrator;

        /**
         * @protected
         * @type {EventBus}
         */
        this.eventBus = eventBus;
    }

    /**
     * @protected
     */
    getEntityName() {
        return 'Online Task';
    }

    /**
     * @protected
     */
    getEntityClass() {
        return OnlineTask;
    }

    /**
     * @protected
     */
    getTableName() {
        return DataStoreNames.ONLINE_TASKS;
    }

    /**
     * @param {string} id
     *
     * @return {Promise<OnlineTask>}
     */
    find(id) {
        return this.dataStore.find(this.getTableName(), id)
            .then((data) => {
                if (!data) {
                    throw new Error(`${this.getEntityName()} ${id} was not found.`);
                }

                return basicHydrate(data, new (this.getEntityClass())());
            });
    }

    /**
     * @return {Promise<OnlineTask[]>}
     */
    findAll() {
        return this.dataStore
            .findAll(this.getTableName())
            .then(createModelBasicHydrator(this.getEntityClass()));
    }

    /**
     * Returns all tasks as JSON without being hydrated.
     *
     * @return {Promise<[]>}
     */
    findAllAsArray() {
        return this.dataStore.findAll(this.getTableName());
    }

    /**
     * @return {Promise<[]>}
     */
    findAllPending() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.PENDING })
            .toArray();
    }

    /**
     * @return {Promise<[]>}
     */
    findAllRunning() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.RUNNING })
            .toArray();
    }

    /**
     * We currently have 4 statuses 'pending', 'running', 'finished', 'failed' so return any tasks that are not failed.
     * Note: 'finished' tasks are deleted upon completion.
     *
     * @return {Promise<[]>}
     */
    findAllCurrent() {
        return this.dataStore.getStore(this.getTableName())
            .where('status').noneOf([OnlineTaskStatus.FAILED])
            .toArray();
    }

    /**
     * @return {Promise<[]>}
     */
    findAllFailed() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.FAILED })
            .toArray();
    }

    /**
     * Returns all rows from the table that have an Active status.
     *
     * @param {string} type
     *
     * @return {Promise<OnlineTask[]>}
     */
    findAllByType(type) {
        return this.dataStore
            .findWhere(this.getTableName(), 'type', type)
            .then(createModelBasicHydrator(this.getEntityClass()));
    }

    /**
     * @returns {Promise<number>}
     */
    countAll() {
        return this.dataStore.getStore(this.getTableName()).count();
    }

    /**
     * Returns the total number of tasks that are not resolved.
     * This means those that are pending, currently running, or finished.
     * Note that finished tasks are deleted upon completion, so those won't stick around long.
     *
     * @returns {Promise<number>}
     */
    countQueued() {
        return this.dataStore.getStore(this.getTableName())
            .where('status')
            .notEqual(OnlineTaskStatus.FAILED)
            .count();
    }

    /**
     * @returns {Promise<number>}
     */
    countRunning() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.RUNNING })
            .count();
    }

    /**
     * @returns {Promise<number>}
     */
    countPending() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.PENDING })
            .count();
    }

    /**
     * @returns {Promise<number>}
     */
    countFailed() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.FAILED })
            .count();
    }

    countAllCurrent() {
        return this.dataStore.getStore(this.getTableName())
            .where('status').noneOf([OnlineTaskStatus.FAILED])
            .count();
    }

    /**
     * @returns {Promise<?OnlineTask>}}
     */
    findNextPendingTask() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.PENDING })
            .sortBy('ID')
            .then((items) => {
                const item = items[0];

                if (item) {
                    return basicHydrate(item, new (this.getEntityClass())());
                }

                return null;
            });
    }

    findNextRunningTask() {
        return this.dataStore.getStore(this.getTableName())
            .where({ status: OnlineTaskStatus.RUNNING })
            .sortBy('ID')
            .then((items) => {
                const item = items[0];

                if (item) {
                    return basicHydrate(item, new (this.getEntityClass())());
                }

                return null;
            });
    }

    /**
     * @param {OnlineTask} task
     *
     * @return {Promise}
     */
    persist(task) {
        const taskObj = {
            ...task,
        };

        // The ID index is auto incrementing, and IDB will throw an error if you try and insert an empty
        // value to it. To avoid that clone the task and delete its ID property.
        if (!task.ID) {
            delete taskObj.ID;
        }

        let p = this.dataStore.persist(this.getTableName(), taskObj);

        p.finally(() => {
            this.eventBus.broadcast(EventNames.ONLINE_QUEUE_MODIFIED);
        });

        return p;
    }

    /**
     * @param {number} id
     *
     * @returns {Promise}
     */
    remove(id) {
        let p = this.dataStore.getStore(this.getTableName()).delete(id);

        p.finally(() => {
            this.eventBus.broadcast(EventNames.ONLINE_QUEUE_MODIFIED);
        });

        return p;
    }
}

module.exports = OnlineTaskRepository;
